<template>
  <section id="projects" class="section-padding bg-white">
    <div class="container-custom">
      <div class="max-w-7xl mx-auto">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <h2 
            class="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 animate-fade-in"
          >
            {{ $t('projects.title') }}
          </h2>
          <p 
            class="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-8 animate-slide-up"
            v-scroll-reveal="{ delay: 200 }"
          >
            {{ $t('projects.subtitle') }}
          </p>
          <div 
            class="w-24 h-1 bg-gradient-to-r from-primary-600 to-accent-600 mx-auto animate-slide-up"
            v-scroll-reveal="{ delay: 400 }"
          ></div>
        </div>

        <!-- Projects Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          <div 
            v-for="(project, index) in displayedProjects"
            :key="project.id"
            class="card group cursor-pointer transform hover:scale-105 transition-all duration-300"
            v-scroll-reveal="{ delay: index * 100 }"
            @click="openProjectModal(project)"
          >
            <!-- Project Image -->
            <div class="relative overflow-hidden h-64 bg-gray-200">
              <img
                v-if="hasValidImage(project.images?.[0])"
                :src="getImageUrl(project.images?.[0])"
                :alt="getLocalizedText(project.title)"
                class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
                @error="handleImageError($event, project)"
              >
              <div v-else class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                <div class="text-center">
                  <svg class="w-24 h-24 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <p class="mt-2 text-sm text-gray-500">暫無圖片</p>
                </div>
              </div>
              
              <!-- Overlay on hover -->
              <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                <div class="p-6 w-full">
                  <button class="btn-primary w-full">
                    {{ $t('projects.viewDetail') }}
                  </button>
                </div>
              </div>

              <!-- Category Badge -->
              <div class="absolute top-4 right-4 px-3 py-1 bg-primary-600 text-white text-sm rounded-full shadow-lg">
                {{ project.category }}
              </div>
            </div>

            <!-- Project Info -->
            <div class="p-6">
              <h3 class="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                {{ getLocalizedText(project.title) }}
              </h3>
              <div class="space-y-2 mb-4">
                <div class="flex items-center text-gray-600 text-sm">
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  {{ getLocalizedText(project.location) }}
                </div>
                <div class="flex items-center text-gray-600 text-sm">
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
                  </svg>
                  {{ project.area }}
                </div>
              </div>
              <p class="text-gray-600 text-sm line-clamp-3">
                {{ getLocalizedText(project.description) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Load More Button -->
        <div class="text-center" v-if="hasMoreProjects">
          <button
            @click="loadMoreProjects"
            class="btn-outline border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white"
            :disabled="isLoading"
          >
            <span v-if="!isLoading">{{ $t('projects.viewAll') }}</span>
            <span v-else class="flex items-center space-x-2">
              <div class="spinner w-5 h-5"></div>
              <span>{{ $t('common.loading') }}</span>
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Project Detail Modal -->
    <Teleport to="body">
      <div
        v-if="selectedProject"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        @click.self="closeProjectModal"
      >
        <div class="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <!-- Modal Header -->
          <div class="relative">
            <!-- Close Button -->
            <button
              @click="closeProjectModal"
              class="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
            >
              <svg class="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>

            <!-- Image Carousel -->
            <div class="relative h-96 bg-gray-200">
              <img
                v-if="hasValidImage(selectedProject.images?.[currentImageIndex])"
                :src="getImageUrl(selectedProject.images?.[currentImageIndex])"
                :alt="getLocalizedText(selectedProject.title)"
                class="w-full h-full object-cover"
                @error="handleImageError($event, selectedProject)"
              >
              <div v-else class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                <div class="text-center">
                  <svg class="w-32 h-32 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <p class="mt-4 text-gray-500">暫無圖片</p>
                </div>
              </div>

              <!-- Navigation Arrows -->
              <button
                v-if="selectedProject.images.length > 1"
                @click="previousImage"
                class="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>
              <button
                v-if="selectedProject.images.length > 1"
                @click="nextImage"
                class="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>

              <!-- Image Indicators -->
              <div class="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                <button
                  v-for="(image, idx) in selectedProject.images"
                  :key="idx"
                  @click="currentImageIndex = idx"
                  :class="[
                    'w-2 h-2 rounded-full transition-all duration-300',
                    idx === currentImageIndex ? 'w-8 bg-white' : 'bg-white/50'
                  ]"
                ></button>
              </div>
            </div>
          </div>

          <!-- Modal Content -->
          <div class="p-8">
            <h3 class="text-3xl font-bold text-gray-900 mb-4">
              {{ getLocalizedText(selectedProject.title) }}
            </h3>

            <!-- Project Meta -->
            <div class="flex flex-wrap gap-4 mb-6">
              <div class="flex items-center text-gray-600">
                <svg class="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                </svg>
                <span>{{ getLocalizedText(selectedProject.location) }}</span>
              </div>
              <div class="flex items-center text-gray-600">
                <svg class="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
                </svg>
                <span>{{ selectedProject.area }}</span>
              </div>
              <div class="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                {{ getLocalizedText(selectedProject.category) }}
              </div>
            </div>

            <!-- Project Description -->
            <div class="prose prose-lg max-w-none mb-8">
              <p class="text-gray-700 leading-relaxed">
                {{ getLocalizedText(selectedProject.description) }}
              </p>
            </div>

            <!-- Video Embed (if exists) -->
            <div v-if="selectedProject.videoUrl" class="mb-8">
              <div class="aspect-video bg-gray-100 rounded-xl overflow-hidden">
                <iframe
                  :src="selectedProject.videoUrl"
                  class="w-full h-full"
                  frameborder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowfullscreen
                ></iframe>
              </div>
            </div>

            <!-- Keywords/Tags -->
            <div v-if="selectedProject.keywords && selectedProject.keywords.length > 0" class="mb-6">
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="keyword in selectedProject.keywords"
                  :key="keyword"
                  class="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                >
                  #{{ keyword }}
                </span>
              </div>
            </div>

            <!-- CTA Button -->
            <div class="mt-8 pt-8 border-t border-gray-200">
              <button
                @click="scrollToSection('contact'); closeProjectModal()"
                class="btn-primary w-full md:w-auto"
              >
                對此案例有興趣？聯絡我們
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </section>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

export default {
  name: 'ProjectsSection',
  setup() {
    const { t, locale } = useI18n()
    const projects = ref([])
    const displayedProjects = ref([])
    const selectedProject = ref(null)
    const currentImageIndex = ref(0)
    const isLoading = ref(false)
    const projectsPerPage = 6
    const currentPage = ref(1)

    // Computed
    const hasMoreProjects = computed(() => {
      return displayedProjects.value.length < projects.value.length
    })

    // Helper function to get localized text
    const getLocalizedText = (field) => {
      if (typeof field === 'string') {
        return field
      }
      if (typeof field === 'object' && field !== null) {
        // Try current locale first, fallback to zh-TW, then any available
        const currentLocale = locale.value
        if (field[currentLocale]) return field[currentLocale]
        if (field['zh-TW']) return field['zh-TW']
        // Return first available value
        const keys = Object.keys(field)
        return keys.length > 0 ? field[keys[0]] : ''
      }
      return field || ''
    }

    // Check if image exists and is valid
    const hasValidImage = (imagePath) => {
      if (!imagePath || imagePath === '') {
        return false
      }
      return true
    }

    // Get proper image URL
    const getImageUrl = (imagePath) => {
      if (!imagePath) {
        return ''
      }

      // If it's already a full URL, return as is
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath
      }

      // For VPS uploads, use relative path (will be proxied by Vite)
      if (imagePath.startsWith('/uploads/')) {
        return imagePath
      }

      // For other relative paths
      return imagePath
    }

    // Handle image load error
    const handleImageError = (event, project) => {
      console.warn('Failed to load image:', event.target.src, 'for project:', project?.id)
      // Hide the failed image
      event.target.style.display = 'none'
      // Show placeholder by letting v-if handle it
      if (project) {
        project.imageLoadFailed = true
      }
    }

    // Fetch projects from public API
    const fetchProjects = async () => {
      try {
        isLoading.value = true
        const currentLocale = locale.value
        const response = await fetch(`/api/public/projects?locale=${currentLocale}&limit=100`)
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const result = await response.json()
        
        if (result.success && result.data) {
          projects.value = result.data
          // Initial display
          displayedProjects.value = projects.value.slice(0, projectsPerPage)
        } else {
          throw new Error('Invalid response format')
        }
      } catch (error) {
        console.error('Error fetching projects:', error)
        // Fallback to mock data for development
        loadMockProjects()
      } finally {
        isLoading.value = false
      }
    }

    // Load mock projects for development
    const loadMockProjects = () => {
      projects.value = [
        {
          id: '1',
          title: '嘉義住宅遮陽工程',
          description: '為住宅庭院打造專業遮陽系統，結合美學設計與結構工程，提供舒適的戶外空間。採用高品質防水布料，耐候性佳，使用年限可達5年以上。',
          location: '嘉義市',
          area: '45 m²',
          category: '住宅',
          images: [
            '/Product data/sample picture/LINE_NOTE_251116_1.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_2.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_3.jpg'
          ],
          videoUrl: '',
          keywords: ['住宅遮陽', '庭院設計', '防水布料']
        },
        {
          id: '2',
          title: '工廠廠房遮陽網施工',
          description: '大型工廠廠房遮陽網工程，有效降低室內溫度8-12°C，提升工作環境舒適度。採用專業鋼構設計，確保結構安全穩固。',
          location: '台南市',
          area: '280 m²',
          category: '工業',
          images: [
            '/Product data/sample picture/LINE_NOTE_251116_4.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_5.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_6.jpg'
          ],
          videoUrl: '',
          keywords: ['工廠遮陽', '大型工程', '溫度控制']
        },
        {
          id: '3',
          title: '學校操場遮陽棚',
          description: '為學校操場打造大型遮陽棚，提供學生安全舒適的戶外活動空間。通過風壓計算與結構設計，確保在各種天候條件下的安全性。',
          location: '高雄市',
          area: '520 m²',
          category: '教育',
          images: [
            '/Product data/sample picture/LINE_NOTE_251116_7.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_8.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_9.jpg'
          ],
          videoUrl: '',
          keywords: ['學校', '操場遮陽', '公共空間']
        },
        {
          id: '4',
          title: '汽車展示中心遮陽',
          description: '汽車展示中心戶外遮陽工程，結合現代美學設計，提升品牌形象同時保護展示車輛免受日曬雨淋。',
          location: '台中市',
          area: '180 m²',
          category: '商業',
          images: [
            '/Product data/sample picture/LINE_NOTE_251116_10.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_11.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_12.jpg'
          ],
          videoUrl: '',
          keywords: ['商業空間', '汽車展示', '品牌形象']
        },
        {
          id: '5',
          title: '餐廳戶外用餐區',
          description: '餐廳戶外座位區遮陽工程，打造舒適優雅的用餐環境。採用透氣性佳的布料，確保空氣流通同時提供良好的遮陽效果。',
          location: '台北市',
          area: '65 m²',
          category: '餐飲',
          images: [
            '/Product data/sample picture/LINE_NOTE_251116_13.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_14.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_15.jpg'
          ],
          videoUrl: '',
          keywords: ['餐廳', '戶外用餐', '商業空間']
        },
        {
          id: '6',
          title: '社區公共空間遮陽',
          description: '社區公共休憩空間遮陽工程，為居民打造舒適的戶外活動區域。採用高強度張力設計，耐用且易於維護。',
          location: '新北市',
          area: '320 m²',
          category: '社區',
          images: [
            '/Product data/sample picture/LINE_NOTE_251116_16.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_17.jpg',
            '/Product data/sample picture/LINE_NOTE_251116_18.jpg'
          ],
          videoUrl: '',
          keywords: ['社區', '公共空間', '休憩區']
        }
      ]

      displayedProjects.value = projects.value.slice(0, projectsPerPage)
    }

    // Load more projects
    const loadMoreProjects = () => {
      currentPage.value++
      const startIndex = 0
      const endIndex = currentPage.value * projectsPerPage
      displayedProjects.value = projects.value.slice(startIndex, endIndex)
    }

    // Modal functions
    const openProjectModal = (project) => {
      selectedProject.value = project
      currentImageIndex.value = 0
      document.body.style.overflow = 'hidden'
    }

    const closeProjectModal = () => {
      selectedProject.value = null
      currentImageIndex.value = 0
      document.body.style.overflow = 'auto'
    }

    // Image navigation
    const nextImage = () => {
      if (selectedProject.value && selectedProject.value.images.length > 0) {
        currentImageIndex.value = (currentImageIndex.value + 1) % selectedProject.value.images.length
      }
    }

    const previousImage = () => {
      if (selectedProject.value && selectedProject.value.images.length > 0) {
        currentImageIndex.value = currentImageIndex.value === 0 
          ? selectedProject.value.images.length - 1 
          : currentImageIndex.value - 1
      }
    }

    // Scroll to section
    const scrollToSection = (sectionId) => {
      const element = document.getElementById(sectionId)
      if (element) {
        const headerHeight = 80
        const elementPosition = element.offsetTop - headerHeight
        
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        })
      }
    }

    // Initialize
    onMounted(() => {
      fetchProjects()

      // Initialize scroll animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated')
          }
        })
      }, observerOptions)

      document.querySelectorAll('[v-scroll-reveal]').forEach(el => {
        observer.observe(el)
      })
    })

    return {
      projects,
      displayedProjects,
      selectedProject,
      currentImageIndex,
      isLoading,
      hasMoreProjects,
      loadMoreProjects,
      openProjectModal,
      closeProjectModal,
      nextImage,
      previousImage,
      scrollToSection,
      getLocalizedText,
      hasValidImage,
      getImageUrl,
      handleImageError
    }
  }
}
</script>

<style scoped>
/* Projects section specific styles */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Modal animations */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Image carousel smooth transitions */
.aspect-video {
  aspect-ratio: 16 / 9;
}

/* Hover effects */
.group:hover .object-cover {
  transform: scale(1.1);
}

/* Loading spinner */
.spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #0ea5e9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .max-h-\[90vh\] {
    max-height: 95vh;
  }
}

/* Backdrop blur */
.backdrop-blur-sm {
  backdrop-filter: blur(4px);
}
</style>